import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import clsx from 'clsx';
import { useCategories } from '../../services/productService';
import { useFilterStore } from '../../stores/filterStore';
import { getCategoryIcon } from '../../lib/categoryIcons';

/**
 * Yelp-style category icon row — circular icons with labels.
 * Wraps into a grid on mobile, scrollable row on larger screens.
 * Uses the `icon` field from each category (Lucide icon name stored in DB).
 */
export function CategoryBar() {
  const { data: categories } = useCategories();
  const navigate = useNavigate();
  const setCategoryId = useFilterStore((s) => s.setCategoryId);

  if (!categories?.length) return null;

  const handleClick = (catId) => {
    setCategoryId(catId);
    navigate('/menu');
  };

  return (
    <section className="bg-brand-white border-b border-ui-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
        {/* Mobile: wrapping grid (4 cols). Tablet+: centered flex row, scrollable if needed */}
        <div className="grid grid-cols-4 gap-3 sm:flex sm:items-start sm:justify-center sm:gap-8 md:gap-10 sm:overflow-x-auto sm:scrollbar-hide">
          {categories.map((cat, i) => {
            const Icon = getCategoryIcon(cat.icon);
            return (
              <motion.button
                key={cat.id}
                onClick={() => handleClick(cat.id)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="flex flex-col items-center gap-1.5 sm:gap-2 min-w-0 sm:min-w-[64px] group cursor-pointer"
              >
                <div className={clsx(
                  'w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center',
                  'bg-brand-light group-hover:bg-brand-red/10 transition-colors'
                )}>
                  <Icon
                    size={22}
                    className="sm:hidden text-brand-dark group-hover:text-brand-red transition-colors"
                  />
                  <Icon
                    size={28}
                    className="hidden sm:block text-brand-dark group-hover:text-brand-red transition-colors"
                  />
                </div>
                <span className="text-[10px] sm:text-xs md:text-sm font-semibold text-brand-dark group-hover:text-brand-red transition-colors text-center leading-tight line-clamp-2">
                  {cat.name}
                </span>
              </motion.button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
